import type {
	Address,
	AddressData,
	Addresses
} from '~~/core/entities/Addresses';

import { AddressService } from '~/services/AddressService';

/**
 * Utilitários para mapeamento dinâmico de endereços com suporte multi-cidade
 */
class AddressUtilsMultiCity {
	private static addressService = AddressService.getInstance();

	/**
	 * Inicializa os utilitários com os dados de endereços
	 */
	static initialize(addresses: Addresses[]): void {
		this.addressService.setAddresses(addresses);
	}

	/**
	 * Retorna o estado de uma cidade específica
	 */
	static getStateByCity(city: string): string | undefined {
		if (!this.addressService) {
			return undefined;
		}
		return this.addressService.getStateByCity(city);
	}

	/**
	 * Retorna a macro-região de uma cidade específica
	 */
	static getMacroregionByCity(state: string, city: string): string | undefined {
		return this.addressService.getMacroregionByCity(state, city);
	}

	/**
	 * Retorna informações completas de uma região
	 */
	static getRegionInfo(
		state: string,
		regionLabel: string
	): AddressData | undefined {
		return this.addressService.getRegionInfo(state, regionLabel);
	}

	/**
	 * Retorna o texto formatado para exibição de endereços
	 */
	static formatAddressDisplay(
		region?: string,
		city?: string,
		state?: string,
		macroregion?: string
	): string {
		if (region && city && state) {
			return `${region}, ${city} - ${state}`;
		}

		if (city && state) {
			return `${city} - ${state}`;
		}

		if (macroregion && state) {
			return `${macroregion} - ${state}`;
		}

		if (state) {
			const defaultCity = this.getDefaultCityForState(state);
			return `${defaultCity} - ${state}`;
		}

		return 'Endereço não encontrado';
	}

	/**
	 * Retorna a cidade padrão para um estado
	 */
	static getDefaultCityForState(state: string): string {
		const cities = this.addressService.getCitiesByState(state);
		return cities[0]?.label || 'Cidade não encontrada';
	}

	/**
	 * Retorna o estado padrão quando não especificado
	 */
	static getDefaultState(): string {
		const states = this.addressService.getAllStates();
		return states[0] || 'SP';
	}

	/**
	 * Verifica se um endereço é válido
	 */
	static isValidAddress(
		state?: string,
		city?: string,
		region?: string,
		macroregion?: string
	): boolean {
		if (!state) return false;

		const stateAddresses = this.addressService.getAddressesByState(state);
		if (!stateAddresses) return false;

		if (city && !this.addressService.hasCityInState(state, city)) {
			return false;
		}

		if (
			macroregion &&
			!this.addressService.hasMacroregionInState(state, macroregion)
		) {
			return false;
		}

		if (region) {
			return this.isValidRegionForAddress(state, region, city, macroregion);
		}

		return true;
	}

	/**
	 * Valida se uma região pertence ao endereço especificado
	 */
	private static isValidRegionForAddress(
		state: string,
		region: string,
		city?: string,
		macroregion?: string
	): boolean {
		const regionInfo = this.addressService.getRegionInfo(state, region);
		if (!regionInfo) return false;

		const regionBelongsToCity = !city || regionInfo.data.city === city;
		const regionBelongsToMacroregion =
			!macroregion || regionInfo.data.macroregion === macroregion;

		return regionBelongsToCity && regionBelongsToMacroregion;
	}

	/**
	 * Retorna informações de contexto para uma região
	 */
	static getRegionContext(
		state: string,
		regionLabel: string
	): {
		state: string;
		city: string;
		macroregion: string;
		region: string;
	} | null {
		const regionInfo = this.addressService.getRegionInfo(state, regionLabel);

		if (!regionInfo) return null;

		return {
			state,
			city: regionInfo.data.city,
			macroregion: regionInfo.data.macroregion,
			region: regionInfo.label
		};
	}

	/**
	 * Retorna informações de contexto para uma cidade
	 */
	static getCityContext(
		state: string,
		city: string
	): {
		state: string;
		city: string;
		macroregion?: string;
	} | null {
		if (!this.addressService.hasCityInState(state, city)) {
			return null;
		}

		const macroregion = this.addressService.getMacroregionByCity(state, city);

		return {
			state,
			city,
			macroregion
		};
	}

	/**
	 * Retorna informações de contexto para uma macro-região
	 */
	static getMacroregionContext(
		state: string,
		macroregion: string
	): {
		state: string;
		macroregion: string;
		cities: string[];
	} | null {
		if (!this.addressService.hasMacroregionInState(state, macroregion)) {
			return null;
		}

		const cities = this.addressService.getCitiesByMacroregion(
			state,
			macroregion
		);

		return {
			state,
			macroregion,
			cities: cities.map(city => city.label)
		};
	}

	/**
	 * Retorna estatísticas dos endereços disponíveis
	 */
	static getAddressStats() {
		return this.addressService.getAddressStats();
	}

	/**
	 * Busca endereços por termo de pesquisa
	 */
	static searchAddresses(searchTerm: string) {
		return this.addressService.searchAddresses(searchTerm);
	}

	/**
	 * Retorna todos os estados disponíveis
	 */
	static getAllStates(): string[] {
		return this.addressService.getAllStates();
	}

	/**
	 * Retorna todas as cidades de um estado
	 */
	static getCitiesByState(state: string): Address[] {
		return this.addressService.getCitiesByState(state);
	}

	/**
	 * Retorna todas as macro-regiões de um estado
	 */
	static getMacroregionsByState(state: string): Address[] {
		return this.addressService.getMacroregionsByState(state);
	}

	/**
	 * Retorna todas as regiões de um estado
	 */
	static getRegionsByState(state: string): AddressData[] {
		return this.addressService.getRegionsByState(state);
	}

	/**
	 * Retorna todas as regiões de uma cidade
	 */
	static getRegionsByCity(state: string, city: string): AddressData[] {
		return this.addressService.getRegionsByCity(state, city);
	}

	/**
	 * Retorna todas as regiões de uma macro-região
	 */
	static getRegionsByMacroregion(
		state: string,
		macroregion: string
	): AddressData[] {
		return this.addressService.getRegionsByMacroregion(state, macroregion);
	}
}

export { AddressUtilsMultiCity };
